import type { AgentEvent, Message, Snapshot } from "../types";
// With the extension: `node --test` loads this module directly and resolves no bare paths.
import { isShown } from "./notices.ts";

/** Two events are about the same message when they carry the same role and timestamp: pi builds
 * the streaming message once and hands out copies of it until the run ends. */
function sameMessage(a: Message | undefined, b: Message): boolean {
	if (!a || a.role !== b.role) return false;
	const at = (a as { timestamp?: unknown }).timestamp;
	const bt = (b as { timestamp?: unknown }).timestamp;
	return typeof at === "number" && at === bt;
}

/** Apply a pi event to the snapshot. */
export function reduce(prev: Snapshot, event: AgentEvent): Snapshot {
	switch (event.type) {
		case "agent_start":
			return { ...prev, isStreaming: true };
		case "agent_end":
			return { ...prev, isStreaming: false };
		case "message_start":
			// A message the chat never draws is left out, or the next update would no longer find
			// the reply it replaces at the end.
			if (!isShown(event.message as Message)) return prev;
			return { ...prev, messages: [...prev.messages, event.message as Message] };
		case "message_update":
		case "message_end": {
			// Replace the message this event is about, not whatever is last: a snapshot taken
			// mid-run may not have seen its message_start.
			const incoming = event.message as Message;
			if (!isShown(incoming)) return prev;
			const messages = prev.messages.slice();
			if (sameMessage(messages[messages.length - 1], incoming)) messages[messages.length - 1] = incoming;
			else messages.push(incoming);
			return { ...prev, messages };
		}
		default:
			return prev;
	}
}
